export const detailedTablePlugin = {
  id: 'detailedTablePlugin',
  afterUpdate(chart, args, options) {
    const container = document.getElementById(options.containerID)
    if (!container) return

    const frag = document.createDocumentFragment()

    // Helpers
    const el = (tag, classes = [], props = {}) => {
      const node = document.createElement(tag)
      if (classes?.length) node.classList.add(...classes)
      Object.assign(node, props)
      return node
    }
    const td = (text = '') => {
      const n = el('td')
      n.textContent = text || '\u00A0'
      return n
    }
    const pick = (v) => (Array.isArray(v) ? v[0] : v)

    // 1) MODE: 'paired' (Base/Compare) ou 'series' (une colonne par dataset)
    const mode =
      options?.mode ?? (chart.config.type === 'bar' ? 'paired' : 'series')
    const orderedStacks = options?.stacks ?? ['base', 'compare'] // utilisé seulement en 'paired'
    const keepPlaceholderForMissingStack = options?.keepPlaceholder ?? true // garde colonne vide si stack absente
    const router = options?.router

    const table = el('table', [], {
      id: 'statistic-table'
    })

    const datasets = chart.data.datasets || []
    const grouped =
      Object.groupBy?.(datasets, (d) => d.label) ??
      datasets.reduce((acc, d) => ((acc[d.label] ||= []).push(d), acc), {})
    const labels = chart.data.labels || []
    const isVisibleIdx = (idx) =>
      chart.isDatasetVisible ? chart.isDatasetVisible(idx) !== false : true

    // Map dataset ref -> index
    const dsIndexByRef = new Map()
    datasets.forEach((ds, i) => dsIndexByRef.set(ds, i))

    // Couleur du groupe (bar: backgroundColor / line: borderColor si dispo)
    const groupColorOf = (firstIdx) =>
      pick(datasets[firstIdx]?.borderColor) ||
      pick(datasets[firstIdx]?.backgroundColor) ||
      '#999'

    // Construit le plan de colonnes selon le mode
    const buildColumns = (groupDatasets) => {
      if (mode === 'paired') {
        const dsByStack = Object.fromEntries(
          orderedStacks.map((stk) => [
            stk,
            groupDatasets.find((d) => d.stack === stk) || null
          ])
        )

        const cols = orderedStacks
          .map((stk) => {
            const ds = dsByStack[stk]
            if (!ds) {
              return keepPlaceholderForMissingStack
                ? {
                    key: `stk:${stk}`,
                    title: stk === 'base' ? 'Base' : 'Compare',
                    details: [], // colonne vide
                    visible: true // on garde la colonne pour ne pas décaler
                  }
                : null
            }
            const idx = dsIndexByRef.get(ds)
            return {
              key: `stk:${stk}`,
              title: stk === 'base' ? 'Base' : 'Compare',
              details: ds.details ?? [],
              visible: isVisibleIdx(idx)
            }
          })
          .filter(Boolean)

        // Si toutes colonnes 'paired' sont invisibles, on rend rien
        if (cols.every((c) => !c.visible)) return []

        // Ne pas afficher les colonnes invisibles
        return cols.filter(
          (c) =>
            c.visible ||
            (c.details.length === 0 && keepPlaceholderForMissingStack)
        )
      }

      // mode === 'series' : une colonne par dataset du groupe
      const cols = groupDatasets
        .map((ds, localIdx) => {
          const idx = dsIndexByRef.get(ds)
          return {
            key: `ds:${idx}`,
            title:
              ds.stack ?? ds.variant ?? ds.subLabel ?? `Série ${localIdx + 1}`,
            details: ds.details ?? [],
            visible: isVisibleIdx(idx)
          }
        })
        .filter((c) => c.visible) // en 'series', on retire ce qui est masqué

      return cols
    }

    // rendu par groupe
    Object.entries(grouped).forEach(([groupLabel, groupDatasets]) => {
      const firstIdx = dsIndexByRef.get(groupDatasets[0]) ?? 0
      const groupColor = groupColorOf(firstIdx)

      const columns = buildColumns(groupDatasets)
      if (!columns || columns.length === 0) return

      const section = el('tbody')

      // Titre de groupe + puce couleur
      const groupTr = el('tr', [])
      const groupTh = el('th', [], {
        colSpan: 1 + columns.length,
        scope: 'rowgroup'
      })
      const titleWrap = el('span')
      const dot = el('span')
      dot.style.cssText = `display:inline-block;width:10px;height:10px;border-radius:9999px;margin-right:6px;background:${groupColor}`
      const title = el('span')
      title.textContent = groupLabel ?? ''
      titleWrap.append(dot, title)
      groupTh.append(titleWrap)
      groupTr.appendChild(groupTh)
      section.appendChild(groupTr)

      // Ligne d'intitulés (Label + titres de colonnes dynamiques)
      const headerRow = el('tr', [])

      headerRow.appendChild(td('Label'))
      columns.forEach((col) => headerRow.appendChild(td(col.title)))
      section.appendChild(headerRow)

      // Données
      for (let j = 0; j < labels.length; j++) {
        // Nombre de sous-lignes max pour ce label
        const lengths = columns.map((col) => col.details?.[j]?.length || 0)
        const max = Math.max(1, ...lengths)

        for (let k = 0; k < max; k++) {
          const row = document.createElement('tr')

          // 1) label répété à chaque sous-ligne (facile pour CSV/Excel)
          const thLabel = el('th', [], { scope: 'row', vAlign: 'top' })
          thLabel.textContent = k === 0 ? (labels[j] ?? '') : ''
          row.appendChild(thLabel)

          // 2) colonnes
          columns.forEach((col) => {
            const rowArr = col.details?.[j] ?? []
            const cellData = rowArr[k]

            if (cellData?.route) {
              const tdLink = document.createElement('td')
              const link = (function makeRouterLink(
                router,
                text,
                to,
                className = 'text-blue-600 hover:underline'
              ) {
                const a = document.createElement('a')
                a.textContent = text
                a.className = className
                if (router && typeof router.resolve === 'function') {
                  const resolved = router.resolve(to)
                  a.href = resolved.href
                  a.addEventListener('click', (e) => {
                    if (
                      e.metaKey ||
                      e.ctrlKey ||
                      e.shiftKey ||
                      e.altKey ||
                      e.button !== 0 ||
                      a.target === '_blank'
                    )
                      return
                    e.preventDefault()
                    router.push(to)
                  })
                } else {
                  a.href = typeof to === 'string' ? to : (to?.path ?? '#')
                }
                return a
              })(router, cellData.title, cellData.route)
              tdLink.appendChild(link)
              row.appendChild(tdLink)
            } else {
              row.appendChild(td(cellData?.title))
            }
          })

          section.appendChild(row)
        }
      }

      table.appendChild(section)
    })

    frag.appendChild(table)
    container.replaceChildren(frag)
  }
}
